const getCurrentMessage = (messages) =>
  messages.length ? messages[messages.length - 1] : false;
const getPreviousMessage = (messages) =>
  messages.length > 1 ? messages[messages.length - 2] : false;

/**
 * Check if something is loading inside iframe (for example page have been changed)
 *
 * Example of incoming messages in case of changing page
 * {type: 'iframeAnchorClick', height: undefined, cycle: undefined, burgerOpen: undefined}
 * {type: 'clearHeight', height: null, cycle: undefined, burgerOpen: undefined}
 * {type: 'height', height: null, cycle: unload, burgerOpen: undefined}
 *
 * @param {Array} messages responses from iframe
 */
export const isSomethingLoadingIsideIframe = (messages) => {
  const message = getCurrentMessage(messages);
  if (!message) return false;

  return !message.height && message.cycle === 'unload';
};

/**
 * This check prevent to change scrollbar height when iframe is loaded.
 *
 * This happens in case then we have those incoming messages:
 * {type: 'burgerHeight', height: undefined, cycle: undefined, burgerOpen: false}
 * {type: 'height', height: 2987, cycle: undefined, burgerOpen: undefined}
 * {type: 'height', height: 3464, cycle: undefined, burgerOpen: undefined}
 *
 * And to solve this issue we should prevent from affecting second message on scrollbar.
 * @param {Array} messages responses from iframe
 */
export const isPreviousMessageAboutClosedBurger = (messages) => {
  const message = getCurrentMessage(messages);
  const prevMessage = getPreviousMessage(messages);

  if (message.type !== 'height' || !prevMessage) return false;

  return (
    prevMessage.type === 'burgerHeight' && prevMessage.burgerOpen === false
  );
};

/**
 * This function for checking if incoming height is same as iframe height
 *
 * Heigh change message example
 * {type: 'height', height: 3464, cycle: undefined, burgerOpen undefined}
 *
 * @param {Array} messages responses from iframe
 * @param {int} height current iframe height
 */
export const isHeightDidNotChange = (messages, height) => {
  const message = getCurrentMessage(messages);
  if (!message) return true;

  return (
    message.type === 'height' && message.height && message.height === height
  );
};

/**
 * This function for checking if incoming height is different from iframe height
 *
 * Heigh change message example
 * {type: 'height', height: 3464, cycle: undefined, burgerOpen undefined}
 *
 * @param {Array} messages responses from iframe
 * @param {int} height current iframe height
 */
export const isHeightDidChange = (messages, height) => {
  const message = getCurrentMessage(messages);
  if (!message) return false;

  return (
    message.type === 'height' && message.height && message.height !== height
  );
};

/**
 * Check if burger menu clicked
 *
 * BurgerMenu message
 * {type: 'burgerHeight', height: undefined, cycle: undefined, burgerOpen: true}
 *
 * @param {Array} messages responses from iframe
 */
export const isBurgerMenuActed = (messages) => {
  const message = getCurrentMessage(messages);
  if (!messages) return false;

  return message.type === 'burgerHeight';
};

/**
 * Check if navigation link was clicked
 *
 * This happens in case then we have those incoming messages:
 * {type: "iframeAnchorClick", offset: 0, hash: ""}
 * {type: "clearHeight", height: null}
 * @param {*} messages responses from iframe
 */
export const isNavClicked = (messages) => {
  const message = getCurrentMessage(messages);
  const prevMessage = getPreviousMessage(messages);
  if (!messages) return false;

  return (
    prevMessage.type === 'iframeAnchorClick' && message.type === 'clearHeight'
  );
};

/**
 * Check if Iframe has been first time loaded
 * (somethink like componentDidMount for template iframe )
 *
 * To check if iframe loaded first time we have to check those messages
 * in the beginning of the messages array:
 * {type: "burgerHeight", burgerOpen: false}
 * {type: "height", height: 2983}
 *
 * Or (if template or template variant does not have burger menu)
 * {type: "height", height: 2983}
 * @param {*} messages responses from iframe
 */
export const isITemplateMounted = (messages) => {
  const message = getCurrentMessage(messages);
  const prevMessage = getPreviousMessage(messages);
  if (!messages) return false;

  const isFirstMessageWasHeight =
    messages.length === 1 && message.type === 'height';

  const isFirstTwoMessagessWasBurgerHeightAndHeight =
    prevMessage.type === 'burgerHeight' &&
    message.type === 'height' &&
    messages.length === 2;

  return isFirstMessageWasHeight || isFirstTwoMessagessWasBurgerHeightAndHeight;
};

export const isMountingTemplateHasBurgerMenu = (messages) => {
  return messages.length === 1 && messages[0].type === 'burgerHeight';
};

export const postAskHeightMessage = (iframe) => {
  if (!iframe) return;

  iframe.current.contentWindow.postMessage(
    {
      type: 'scrolling',
    },
    '*'
  );
};

export const postScrollMessage = (iframe, scrollTop) => {
  if (!iframe) return;

  iframe.current.contentWindow.postMessage(
    {
      type: 'onScroll',
      scrollTop,
    },
    '*'
  );
};

export const postScrollMessageStop = (iframe) => {
  if (!iframe) return;

  iframe.current.contentWindow.postMessage(
    {
      type: 'stopScroll',
    },
    '*'
  );
};

export const addMessage = (message, messages) =>
  message.type ? [...messages, message] : messages;
